import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { apiService } from '../services/api';
import { authService } from '../services/authService';
import { getApiFailureMessage, getUnifiedApiErrorMessage } from '../utils/apiErrorMessage';
import './MyPage.css';

interface WebAuthnCredentialInfo {
  id: number;
  deviceName: string | null;
  authenticatorAttachment: string | null;
  transports: string | null;
  lastUsedAt: string | null;
  createdAt: string;
}

const MyPage: React.FC = () => {
  const { user, logout, refreshUser } = useAuth();
  const [credentials, setCredentials] = useState<WebAuthnCredentialInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAddingCredential, setIsAddingCredential] = useState(false);
  const [deviceName, setDeviceName] = useState('');
  const [editingCredentialId, setEditingCredentialId] = useState<number | null>(null);
  const [editingDeviceName, setEditingDeviceName] = useState('');
  
  // 개인정보 수정 관련 상태
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    nickname: user?.nickname || '',
    email: user?.email || ''
  });
  
  // 비밀번호 수정 관련 상태
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    loadCredentials();
  }, []);

  const loadCredentials = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await apiService.api.get('/auth/webauthn/credentials');
      if (response.data.success && response.data.data) {
        setCredentials(response.data.data);
      }
    } catch (err: any) {
      console.error('패스키 목록 조회 실패:', err);
      setError(getApiFailureMessage(err, '패스키 목록을 불러오는데 실패했습니다.'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddCredential = async () => {
    if (!deviceName.trim()) {
      setError('디바이스 이름을 입력해주세요.');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // 1. 패스키 추가 시작
      const startResponse = await apiService.api.post('/auth/webauthn/credentials/add/start', null, {
        params: { deviceName: deviceName.trim() }
      });

      if (!startResponse.data.success || !startResponse.data.data) {
        throw new Error(getUnifiedApiErrorMessage(startResponse.data));
      }

      const challengeData = startResponse.data.data;

      // 2. 브라우저에서 Credential 생성
      const publicKeyCredentialCreationOptions: PublicKeyCredentialCreationOptions = {
        challenge: authService.base64UrlToArrayBuffer(challengeData.challenge),
        rp: {
          id: challengeData.rp.id,
          name: challengeData.rp.name
        },
        user: {
          id: authService.base64UrlToArrayBuffer(challengeData.user.id),
          name: challengeData.user.name,
          displayName: challengeData.user.displayName
        },
        pubKeyCredParams: challengeData.pubKeyCredParams.map((param: any) => ({
          type: param.type as PublicKeyCredentialType,
          alg: param.alg
        })),
        timeout: challengeData.timeout,
        attestation: challengeData.attestation as AttestationConveyancePreference,
        authenticatorSelection: {
          userVerification: challengeData.authenticatorSelection.userVerification as UserVerificationRequirement,
          residentKey: challengeData.authenticatorSelection.residentKey as ResidentKeyRequirement
        }
      };

      const credential = await navigator.credentials.create({
        publicKey: publicKeyCredentialCreationOptions
      }) as PublicKeyCredential | null;

      if (!credential || !(credential instanceof PublicKeyCredential)) {
        throw new Error('인증에 실패했습니다.');
      }

      const response = credential.response as AuthenticatorAttestationResponse;

      // 3. Credential을 Base64로 변환
      const credentialId = authService.arrayBufferToBase64Url(credential.rawId);
      const clientDataJSON = authService.arrayBufferToBase64Url(response.clientDataJSON);
      const attestationObject = authService.arrayBufferToBase64Url(response.attestationObject);
      const transports = response.getTransports ? response.getTransports() : [];

      // 4. 패스키 추가 완료
      const finishResponse = await apiService.api.post('/auth/webauthn/credentials/add/finish', {
        challenge: challengeData.challenge,
        credential: {
          id: credentialId,
          rawId: credentialId,
          response: {
            clientDataJSON,
            attestationObject,
            transports: transports as string[]
          },
          type: credential.type,
          authenticatorAttachment: (credential as any).authenticatorAttachment
        }
      });

      if (finishResponse.data.success) {
        await loadCredentials();
        setIsAddingCredential(false);
        setDeviceName('');
        alert('패스키가 성공적으로 추가되었습니다.');
      } else {
        throw new Error(getUnifiedApiErrorMessage(finishResponse.data));
      }
    } catch (err: any) {
      console.error('패스키 추가 실패:', err);
      setError(getApiFailureMessage(err, '패스키 추가에 실패했습니다.'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateCredentialName = async (credentialId: number) => {
    if (!editingDeviceName.trim()) {
      setError('디바이스 이름을 입력해주세요.');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      await apiService.api.post(`/auth/webauthn/credentials/${credentialId}/name/update`, {
        deviceName: editingDeviceName.trim()
      });
      await loadCredentials();
      setEditingCredentialId(null);
      setEditingDeviceName('');
      alert('패스키 이름이 수정되었습니다.');
    } catch (err: any) {
      console.error('패스키 이름 수정 실패:', err);
      setError(getApiFailureMessage(err, '패스키 이름 수정에 실패했습니다.'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteCredential = async (credentialId: number) => {
    if (!window.confirm('이 패스키를 삭제하시겠습니까?')) {
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      await apiService.api.post(`/auth/webauthn/credentials/${credentialId}/delete`);
      await loadCredentials();
      alert('패스키가 삭제되었습니다.');
    } catch (err: any) {
      console.error('패스키 삭제 실패:', err);
      setError(getApiFailureMessage(err, '패스키 삭제에 실패했습니다.'));
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('ko-KR');
  };

  const getAuthenticatorType = (attachment: string | null) => {
    if (!attachment) return '알 수 없음';
    return attachment === 'platform' ? '내장 인증기 (Face ID, Touch ID 등)' : '외부 인증기 (USB 키 등)';
  };

  const handleUpdateProfile = async () => {
    if (!profileData.name || !profileData.nickname || !profileData.email) {
      setError('모든 필드를 입력해주세요.');
      return;
    }

    // 이메일 형식 검증
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(profileData.email)) {
      setError('올바른 이메일 형식을 입력해주세요.');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      const response = await apiService.updateUserProfile({
        name: profileData.name,
        nickname: profileData.nickname,
        email: profileData.email
      });

      if (response.success && response.data) {
        // API 응답에서 받은 업데이트된 사용자 정보 사용
        const updatedUserInfo = response.data;
        
        // 사용자 정보 업데이트
        if (user && user.id) {
          const updatedUser = {
            id: updatedUserInfo.id || user.id,
            username: updatedUserInfo.username || user.username,
            name: updatedUserInfo.name || profileData.name,
            nickname: updatedUserInfo.nickname || profileData.nickname,
            email: updatedUserInfo.email || profileData.email,
            role: user.role || 'USER',
            status: user.status || 'ACTIVE',
            emailVerified: user.emailVerified || false,
            phoneVerified: user.phoneVerified || false,
            lastLoginAt: user.lastLoginAt || new Date().toISOString()
          };
          
          // AuthContext의 사용자 정보 업데이트
          refreshUser(updatedUser);
        }
        
        // 프로필 데이터도 업데이트
        setProfileData({
          name: updatedUserInfo.name || profileData.name,
          nickname: updatedUserInfo.nickname || profileData.nickname,
          email: updatedUserInfo.email || profileData.email
        });
        
        alert('개인정보가 성공적으로 수정되었습니다.');
        setIsEditingProfile(false);
      } else {
        throw new Error(getUnifiedApiErrorMessage(response));
      }
    } catch (err: any) {
      console.error('개인정보 수정 실패:', err);
      setError(getApiFailureMessage(err, '개인정보 수정에 실패했습니다.'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      setError('모든 필드를 입력해주세요.');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('새 비밀번호가 일치하지 않습니다.');
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setError('새 비밀번호는 8자 이상이어야 합니다.');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      const response = await apiService.changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });

      if (response.success) {
        alert('비밀번호가 성공적으로 변경되었습니다.');
        setIsChangingPassword(false);
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      } else {
        throw new Error(getUnifiedApiErrorMessage(response));
      }
    } catch (err: any) {
      console.error('비밀번호 변경 실패:', err);
      setError(getApiFailureMessage(err, '비밀번호 변경에 실패했습니다.'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || '',
        nickname: user.nickname || '',
        email: user.email || ''
      });
    }
  }, [user]);

  return (
    <div className="mypage-container">
      <div className="mypage-content">
        <h2>마이페이지</h2>

        {user && (
          <div className="user-info-section">
            <div className="section-header">
              <h3>개인정보</h3>
              <button
                onClick={() => setIsEditingProfile(!isEditingProfile)}
                className="edit-button"
              >
                {isEditingProfile ? '취소' : '수정'}
              </button>
            </div>

            {isEditingProfile ? (
              <div className="edit-form">
                {error && (
                  <div className="error-message">{error}</div>
                )}
                <div className="form-group">
                  <label>계정 ID</label>
                  <input type="text" value={user.username} disabled />
                  <small>계정 ID는 변경할 수 없습니다.</small>
                </div>
                <div className="form-group">
                  <label>이름 *</label>
                  <input
                    type="text"
                    value={profileData.name}
                    onChange={(e) => {
                      setProfileData({ ...profileData, name: e.target.value });
                      setError(null);
                    }}
                    placeholder="이름을 입력하세요"
                  />
                </div>
                <div className="form-group">
                  <label>닉네임 *</label>
                  <input
                    type="text"
                    value={profileData.nickname}
                    onChange={(e) => {
                      setProfileData({ ...profileData, nickname: e.target.value });
                      setError(null);
                    }}
                    placeholder="닉네임을 입력하세요"
                  />
                </div>
                <div className="form-group">
                  <label>이메일 *</label>
                  <input
                    type="email"
                    value={profileData.email}
                    onChange={(e) => {
                      setProfileData({ ...profileData, email: e.target.value });
                      setError(null);
                    }}
                    placeholder="이메일을 입력하세요"
                  />
                </div>
                <div className="form-actions">
                  <button onClick={handleUpdateProfile} disabled={isLoading} className="save-button">
                    {isLoading ? '저장 중...' : '저장'}
                  </button>
                  <button 
                    onClick={() => {
                      setIsEditingProfile(false);
                      setError(null);
                      // 원래 값으로 복원
                      if (user) {
                        setProfileData({
                          name: user.name || '',
                          nickname: user.nickname || '',
                          email: user.email || ''
                        });
                      }
                    }} 
                    className="cancel-button"
                    disabled={isLoading}
                  >
                    취소
                  </button>
                </div>
              </div>
            ) : (
              <div className="user-info">
                <p><strong>계정 ID:</strong> {user.username}</p>
                <p><strong>이름:</strong> {user.name || '-'}</p>
                <p><strong>닉네임:</strong> {user.nickname}</p>
                <p><strong>이메일:</strong> {user.email}</p>
              </div>
            )}
          </div>
        )}

        {user && (
          <div className="password-section">
            <div className="section-header">
              <h3>비밀번호 변경</h3>
              <button
                onClick={() => setIsChangingPassword(!isChangingPassword)}
                className="edit-button"
              >
                {isChangingPassword ? '취소' : '변경'}
              </button>
            </div>

            {isChangingPassword && (
              <div className="edit-form">
                {error && (
                  <div className="error-message">{error}</div>
                )}
                <div className="form-group">
                  <label>현재 비밀번호 *</label>
                  <input
                    type="password"
                    value={passwordData.currentPassword}
                    onChange={(e) => {
                      setPasswordData({ ...passwordData, currentPassword: e.target.value });
                      setError(null);
                    }}
                    placeholder="현재 비밀번호를 입력하세요"
                  />
                </div>
                <div className="form-group">
                  <label>새 비밀번호 *</label>
                  <input
                    type="password"
                    value={passwordData.newPassword}
                    onChange={(e) => {
                      setPasswordData({ ...passwordData, newPassword: e.target.value });
                      setError(null);
                    }}
                    placeholder="새 비밀번호를 입력하세요"
                  />
                  <small>8자 이상 입력해주세요.</small>
                </div>
                <div className="form-group">
                  <label>새 비밀번호 확인 *</label>
                  <input
                    type="password"
                    value={passwordData.confirmPassword}
                    onChange={(e) => {
                      setPasswordData({ ...passwordData, confirmPassword: e.target.value });
                      setError(null);
                    }}
                    placeholder="새 비밀번호를 다시 입력하세요"
                  />
                </div>
                <div className="form-actions">
                  <button onClick={handleChangePassword} disabled={isLoading} className="save-button">
                    {isLoading ? '변경 중...' : '변경'}
                  </button>
                  <button 
                    onClick={() => {
                      setIsChangingPassword(false);
                      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                      setError(null);
                    }} 
                    className="cancel-button"
                    disabled={isLoading}
                  >
                    취소
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="credentials-section">
          <div className="section-header">
            <h3>등록된 패스키</h3>
            <button
              onClick={() => setIsAddingCredential(!isAddingCredential)}
              disabled={isLoading}
              className="add-button"
            >
              {isAddingCredential ? '취소' : '+ 패스키 추가'}
            </button>
          </div>

          {isAddingCredential && (
            <div className="add-credential-form">
              <input
                type="text"
                placeholder="디바이스 이름 (예: iPhone 14, Chrome on Windows)"
                value={deviceName}
                onChange={(e) => setDeviceName(e.target.value)}
                disabled={isLoading}
                style={{ marginBottom: '10px', padding: '8px', width: '100%' }}
              />
              <button
                onClick={handleAddCredential}
                disabled={isLoading || !deviceName.trim()}
                className="submit-button"
              >
                {isLoading ? '추가 중...' : '패스키 추가'}
              </button>
            </div>
          )}

          {error && (
            <div className="error-message" style={{ marginTop: '10px', color: 'red' }}>
              {error}
            </div>
          )}

          {isLoading && !credentials.length ? (
            <p>로딩 중...</p>
          ) : credentials.length === 0 ? (
            <p>등록된 패스키가 없습니다.</p>
          ) : (
            <div className="credentials-list">
              {credentials.map((credential) => (
                <div key={credential.id} className="credential-item">
                  <div className="credential-info">
                    {editingCredentialId === credential.id ? (
                      <div className="edit-credential-name">
                        <input
                          type="text"
                          value={editingDeviceName}
                          onChange={(e) => setEditingDeviceName(e.target.value)}
                          placeholder="디바이스 이름"
                          disabled={isLoading}
                          style={{ marginBottom: '10px', padding: '8px', width: '100%' }}
                        />
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button
                            onClick={() => handleUpdateCredentialName(credential.id)}
                            disabled={isLoading || !editingDeviceName.trim()}
                            className="save-button"
                            style={{ fontSize: '0.9rem', padding: '0.5rem 1rem' }}
                          >
                            저장
                          </button>
                          <button
                            onClick={() => {
                              setEditingCredentialId(null);
                              setEditingDeviceName('');
                            }}
                            disabled={isLoading}
                            className="cancel-button"
                            style={{ fontSize: '0.9rem', padding: '0.5rem 1rem' }}
                          >
                            취소
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <h4>{credential.deviceName || '알 수 없는 디바이스'}</h4>
                        <p><strong>타입:</strong> {getAuthenticatorType(credential.authenticatorAttachment)}</p>
                        <p><strong>등록일:</strong> {formatDate(credential.createdAt)}</p>
                        <p><strong>마지막 사용:</strong> {formatDate(credential.lastUsedAt)}</p>
                      </>
                    )}
                  </div>
                  {editingCredentialId !== credential.id && (
                    <div className="credential-actions">
                      <button
                        onClick={() => {
                          setEditingCredentialId(credential.id);
                          setEditingDeviceName(credential.deviceName || '');
                        }}
                        disabled={isLoading}
                        className="edit-button"
                        style={{ fontSize: '0.9rem', padding: '0.5rem 1rem', marginRight: '0.5rem' }}
                      >
                        이름 수정
                      </button>
                      <button
                        onClick={() => handleDeleteCredential(credential.id)}
                        disabled={isLoading || credentials.length <= 1}
                        className="delete-button"
                        title={credentials.length <= 1 ? '최소 1개의 패스키는 유지해야 합니다.' : ''}
                      >
                        삭제
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="logout-section">
          <button onClick={logout} className="logout-button">
            로그아웃
          </button>
        </div>
      </div>
    </div>
  );
};

export default MyPage;

